import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AffectedAreas,
  DistributionLists,
  Me,
  Tickets,
  Users,
  type AffectedAreaRow,
  type AttachmentRecord,
  type DistributionList,
  type IntakeChannel,
  type PatchTicketBody,
  type TicketFull,
  type TicketStatus,
  type TicketPriority,
  type TicketArea,
  type TicketCause,
  type UserSummary,
} from '../lib/api';
import { useTicketEvents } from '../lib/events';
import { UserPicker } from '../components/UserPicker';
import { ListMultiPicker } from '../components/ListPicker';
import { TicketLinks } from '../components/TicketLinks';
import { Attachments } from '../components/Attachments';
import { uploadAttachmentFile, getAttachmentDownloadURL } from '../lib/storage';
import { EmailChipsInput } from '../components/EmailChipsInput';
import { PingDialog } from '../components/PingDialog';
import { NotResolvedDialog } from '../components/NotResolvedDialog';
import { TicketLinkTextarea } from '../components/TicketLinkTextarea';
import { MentionTextarea } from '../components/MentionTextarea';
import { TextWithTicketLinks } from '../components/TextWithTicketLinks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CustomerServiceBadge, StatusBadge } from '../components/Badge';
import { ProjectBreadcrumb } from '../components/ProjectBreadcrumb';
import { statusLabel, priorityLabel, isDueToday } from '../lib/ticket-ui';
import { confirmDialog, promptDialog } from '../components/ui/confirm';

type Tab = 'overview' | 'steps' | 'activity' | 'ai-notes' | 'attachments' | 'links';

interface PendingImage {
  tempId: string;
  filename: string;
  /** Object-URL preview from the local File; revoked on remove / post. */
  previewUrl: string;
  /** 0..1 upload progress. */
  progress: number;
  /** Set when upload + API confirm succeed; this is what we send on post. */
  attachmentId?: string;
  /** Storage path — kept so we can delete the orphan if the user removes the preview after upload. */
  storagePath?: string;
  error?: string;
}

const STATUS_OPTIONS: TicketStatus[] = [
  'OPEN',
  'IN_PROGRESS',
  'BLOCKED',
  'PENDING_CODE_REVIEW',
  'READY_TO_DEPLOY',
  'PENDING_CUSTOMER_REVIEW',
  'PENDING_REPORTER_REVIEW',
  'PENDING_VERIFICATION',
  'FUTURE',
  'RESOLVED',
];

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const ticketId = Number(id);
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketFull | null>(null);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [lists, setLists] = useState<DistributionList[]>([]);
  const [allAreas, setAllAreas] = useState<AffectedAreaRow[]>([]);
  const [me, setMe] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Initial tab can come from a ?tab= query param (e.g. notification deep-link
  // to /tickets/123?tab=activity#comment-456). Falls back to overview.
  const [searchParams] = useSearchParams();
  const initialTab: Tab = (() => {
    const q = searchParams.get('tab');
    return q === 'steps' || q === 'activity' || q === 'attachments' || q === 'links'
      ? q
      : 'overview';
  })();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [comment, setComment] = useState('');
  const [commentMentions, setCommentMentions] = useState<string[]>([]);
  // Pasted-image uploads in flight for the comment-in-progress. Cleared on post.
  // Each entry is uploaded to storage immediately + confirmed against the API
  // as an orphan ticket attachment; on post the comment is associated with
  // them via attachmentIds and the server flips their commentId in one
  // transaction. If the user abandons the comment, the orphan attachments
  // stay on the ticket (visible in Attachments tab) — same trade-off the
  // existing drag-and-drop already makes.
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [postingComment, setPostingComment] = useState(false);
  const [pingOpen, setPingOpen] = useState(false);

  async function load() {
    try {
      const res = await Tickets.get(ticketId);
      setTicket(res.ticket);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    Users.list().then((r) => setUsers(r.users)).catch(() => undefined);
    DistributionLists.list().then((r) => setLists(r.lists)).catch(() => undefined);
    AffectedAreas.list().then((r) => setAllAreas(r.affectedAreas)).catch(() => undefined);
    Me.get().then((r) => setMe(r.user)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Notification deep-links land here with #comment-{id} in the URL. The
  // comment only renders once the activity tab is selected and the ticket
  // data is loaded, so we scroll after the next paint.
  useEffect(() => {
    if (tab !== 'activity' || !ticket) return;
    const hash = window.location.hash;
    if (!hash.startsWith('#comment-')) return;
    const id = hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [tab, ticket]);

  useTicketEvents((msg) => {
    if (msg.ticketId === ticketId) void load();
  });

  async function patch(body: Parameters<typeof Tickets.patch>[1]) {
    try {
      await Tickets.patch(ticketId, body);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function claim() {
    try {
      await Tickets.claim(ticketId);
      await load();
    } catch (e) {
      // 409 → race with another user who claimed first. Reload to pick up the new assignee.
      const msg = e instanceof Error ? e.message : 'Claim failed';
      setError(msg);
      await load();
    }
  }

  async function sendPing(userIds: string[], message: string | null) {
    await Tickets.ping(ticketId, { userIds, message: message ?? undefined });
    setPingOpen(false);
    await load();
  }

  async function postComment() {
    if (!comment.trim() || postingComment) return;
    // Pasted images that haven't finished uploading shouldn't ship with the
    // comment — the post button is disabled while they're in flight, but
    // belt-and-suspenders here too.
    if (pendingImages.some((p) => !p.attachmentId && !p.error)) return;
    setPostingComment(true);
    try {
      const attachmentIds = pendingImages
        .filter((p) => !!p.attachmentId)
        .map((p) => p.attachmentId!);
      await Tickets.comment(ticketId, comment.trim(), commentMentions, attachmentIds);
      // Revoke object URLs now that the previews are gone.
      for (const p of pendingImages) URL.revokeObjectURL(p.previewUrl);
      setComment('');
      setCommentMentions([]);
      setPendingImages([]);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Comment failed');
    } finally {
      setPostingComment(false);
    }
  }

  function handlePastedImages(files: File[]) {
    for (const file of files) {
      const tempId = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);
      const filename =
        file.name && file.name !== 'image.png'
          ? file.name
          : `pasted-${new Date().toISOString().replace(/[:.]/g, '-')}.${
              file.type.split('/')[1] ?? 'png'
            }`;
      setPendingImages((prev) => [
        ...prev,
        { tempId, filename, previewUrl, progress: 0 },
      ]);
      // Upload + confirm in the background; update the matching entry as we go.
      void (async () => {
        try {
          const { storagePath } = await uploadAttachmentFile(
            ticketId,
            file,
            filename,
            (pct) => {
              setPendingImages((prev) =>
                prev.map((p) => (p.tempId === tempId ? { ...p, progress: pct } : p)),
              );
            },
          );
          const confirmed = await Tickets.confirmAttachment(ticketId, {
            storagePath,
            filename,
            mimeType: file.type || 'image/png',
            sizeBytes: file.size,
          });
          setPendingImages((prev) =>
            prev.map((p) =>
              p.tempId === tempId
                ? {
                    ...p,
                    progress: 1,
                    storagePath,
                    attachmentId: confirmed.attachment.id,
                  }
                : p,
            ),
          );
        } catch (e) {
          setPendingImages((prev) =>
            prev.map((p) =>
              p.tempId === tempId
                ? { ...p, error: e instanceof Error ? e.message : 'Upload failed' }
                : p,
            ),
          );
        }
      })();
    }
  }

  async function removePendingImage(tempId: string) {
    const entry = pendingImages.find((p) => p.tempId === tempId);
    if (!entry) return;
    URL.revokeObjectURL(entry.previewUrl);
    setPendingImages((prev) => prev.filter((p) => p.tempId !== tempId));
    // If we already confirmed an orphan attachment, delete it so it doesn't
    // clutter the Attachments tab.
    if (entry.attachmentId) {
      try {
        await Tickets.removeAttachment(ticketId, entry.attachmentId);
      } catch {
        /* best-effort */
      }
    }
  }

  async function handleDelete() {
    if (!ticket) return;
    const ok = await confirmDialog({
      title: `Delete ticket #${ticket.id}?`,
      body: `"${ticket.title}" will be permanently deleted. This cannot be undone.`,
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await Tickets.remove(ticket.id);
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error && !ticket) return <p className="text-sm text-red-600">{error}</p>;
  if (!ticket) return <p className="text-sm text-slate-500">Ticket not found.</p>;

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'steps', label: 'Steps' },
    { id: 'activity', label: 'Activity', count: ticket.comments.length + ticket.events.length },
    { id: 'ai-notes', label: 'AI Notes', count: ticket.aiNotes ? 1 : undefined },
    { id: 'attachments', label: 'Attachments', count: ticket.attachments.length },
    { id: 'links', label: 'Links', count: ticket.outgoingLinks.length + ticket.incomingLinks.length },
  ];

  const hasCustomer =
    !!(ticket.customerCompany || ticket.customerContact || ticket.customerEmail || ticket.customerPhone);
  // Customer service tickets always show the customer section expanded — even when empty —
  // so the user is prompted to fill it in.
  const showCustomerExpanded = hasCustomer || ticket.isCustomerService;

  const currentListIds = (ticket.notifyLists ?? []).map((nl) => nl.listId);
  const currentNotifyEmails = (ticket.notifyEmails ?? []).map((n) => n.email);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <p className="text-xs text-slate-500">Ticket #{ticket.id}</p>
          {ticket.isCustomerService && <CustomerServiceBadge />}
        </div>
        <TitleSection ticket={ticket} onSaved={load} />
        <div className="mt-2 flex flex-wrap gap-2 items-center text-xs">
          <StatusBadge status={ticket.status} />
          <span className="text-slate-600">{priorityLabel[ticket.priority]} priority</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-600">{ticket.area.replace('_', ' ').toLowerCase()}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-600">reported by {ticket.reporter.name}</span>
          {ticket.status === 'BLOCKED' && (
            <button
              type="button"
              onClick={() => setPingOpen(true)}
              className="ml-1 px-2 py-0.5 rounded bg-rose-600 text-white hover:bg-rose-700 text-xs font-medium"
              title="Email a teammate asking for a status update"
            >
              Request status update
            </button>
          )}
        </div>
        {/* Own block, not folded into the flex-wrap row above: this arrives
            after first paint, and that row holds a clickable button that would
            shift under the user if the strip appeared inline. */}
        <ProjectBreadcrumb ticketId={ticket.id} />
        {ticket.status === 'BLOCKED' && (
          <BlockedReasonBanner ticket={ticket} onChange={patch} />
        )}
        {(ticket.status === 'PENDING_REPORTER_REVIEW' ||
          ticket.status === 'PENDING_CUSTOMER_REVIEW') &&
          me &&
          (me.role === 'ADMIN' || me.id === ticket.reporterUserId) && (
            <ReviewActionBar
              ticket={ticket}
              onResolved={() => void handleStatusChange('RESOLVED', ticket, patch, setError)}
              onReopen={async (reason) => {
                await Tickets.reopen(ticket.id, reason);
                await load();
              }}
            />
          )}
      </div>

      {pingOpen && (
        <PingDialog
          users={users}
          suggestedUserIds={[ticket.assigneeUserId, ticket.causeUserId].filter(
            (id): id is string => !!id && id !== me?.id,
          )}
          onCancel={() => setPingOpen(false)}
          onSend={sendPing}
        />
      )}

      {/* Order due date + rush — drives triage priority, kept above customer info */}
      <RushDueBar ticket={ticket} onChange={patch} />

      {/* Customer info — prominent under header, always editable */}
      <CustomerCard ticket={ticket} onChange={patch} expanded={showCustomerExpanded} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="border-b border-slate-200 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                    tab === t.id
                      ? 'border-brand-600 text-brand-700 font-medium'
                      : 'border-transparent text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {t.label}
                  {typeof t.count === 'number' && (
                    <span className="ml-1.5 text-xs text-slate-500">{t.count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {tab === 'overview' && (
            <div className="space-y-4">
              <DescriptionSection ticket={ticket} onSaved={load} />
              <ResolutionSection ticket={ticket} me={me} onSaved={load} />
            </div>
          )}

          {tab === 'steps' && (
            <StepsTab ticketId={ticketId} initial={ticket.stepsToReproduce ?? ''} onSaved={load} />
          )}

          {tab === 'activity' && <ActivityFeed ticket={ticket} />}

          {tab === 'ai-notes' && <AiNotesTab ticket={ticket} onChange={load} />}

          {tab === 'attachments' && (
            <Attachments ticketId={ticketId} attachments={ticket.attachments} onChange={load} />
          )}

          {tab === 'links' && (
            <TicketLinks
              ticketId={ticketId}
              outgoing={ticket.outgoingLinks}
              incoming={ticket.incomingLinks}
              onChange={load}
            />
          )}

          {/* Comment composer — always available, so commenting is one action
              from any tab (not buried behind the Activity tab). */}
          <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
            <MentionTextarea
              value={comment}
              onChange={setComment}
              users={users.filter((u) => u.id !== me?.id)}
              mentions={commentMentions}
              onMentionsChange={setCommentMentions}
              onImagePaste={handlePastedImages}
              placeholder="Add a comment… (type # to link a ticket, @ to mention someone, paste screenshots)"
              rows={3}
              className="input"
            />
            {pendingImages.length > 0 && (
              <PendingImageStrip
                items={pendingImages}
                onRemove={(id) => void removePendingImage(id)}
              />
            )}
            <div className="flex justify-end">
              <button
                onClick={postComment}
                disabled={
                  !comment.trim() ||
                  postingComment ||
                  pendingImages.some((p) => !p.attachmentId && !p.error)
                }
                className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
              >
                {postingComment ? 'Posting…' : 'Post comment'}
              </button>
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          {/* Primary properties — always visible, each one action. */}
          <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
            <Panel label="Status">
              <select
                value={ticket.status}
                onChange={(e) => void handleStatusChange(e.target.value as TicketStatus, ticket, patch, setError)}
                className="input"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o} value={o}>{statusLabel[o]}</option>
                ))}
              </select>
            </Panel>
            <Panel label="Priority">
              <select
                value={ticket.priority}
                onChange={(e) => void patch({ priority: e.target.value as TicketPriority })}
                className="input"
              >
                {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as TicketPriority[]).map((o) => (
                  <option key={o} value={o}>{priorityLabel[o]}</option>
                ))}
              </select>
            </Panel>
            <Panel label="Assignee">
              {me?.role === 'ADMIN' ? (
                <UserPicker
                  users={users}
                  value={ticket.assigneeUserId}
                  onChange={(v) => void patch({ assigneeUserId: v })}
                  allowEmpty
                  emptyLabel="Unassigned"
                />
              ) : (
                <div className="input bg-slate-50 text-slate-700">
                  {ticket.assignee?.name ?? <span className="text-slate-500">Unassigned</span>}
                  {ticket.assignee && (
                    <span className="ml-2 text-xs text-slate-500">(admin only)</span>
                  )}
                </div>
              )}
              {!ticket.assigneeUserId && me && (
                <button
                  type="button"
                  onClick={() => void claim()}
                  className="mt-2 text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Claim — assign to me
                </button>
              )}
            </Panel>
          </div>

          {/* Everything secondary lives behind one disclosure. */}
          <details className="group bg-white border border-slate-200 rounded-lg">
            <summary className="cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center justify-between">
              <span>More details</span>
              <span className="text-slate-500 text-xs transition-transform group-open:rotate-180" aria-hidden="true">▾</span>
            </summary>
            <div className="px-3 pb-3 pt-1 space-y-4 border-t border-slate-100">
              {me?.role === 'ADMIN' && (
                <Panel label="Priority rank">
                  <RankInput
                    value={ticket.rank}
                    onSave={(next) => void patch({ rank: next })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Lower number = higher priority. Leave blank to remove from the queue.
                  </p>
                </Panel>
              )}
              <Panel label="Area">
                <select
                  value={ticket.area}
                  onChange={(e) => void patch({ area: e.target.value as TicketArea })}
                  className="input"
                >
                  <option value="CUSTOMER_WEBSITE">Customer website</option>
                  <option value="PRODUCTION">Production</option>
                  <option value="OTHER">Other</option>
                </select>
              </Panel>
              <Panel label="Source">
                <select
                  value={ticket.intakeChannel}
                  onChange={(e) => void patch({ intakeChannel: e.target.value as IntakeChannel })}
                  className="input"
                >
                  <option value="EMAIL">Email</option>
                  <option value="PHONE">Phone</option>
                  <option value="IN_PERSON">In person</option>
                  <option value="OTHER">Other</option>
                </select>
              </Panel>
              <Panel label="Affected">
                <AffectedAreasPicker
                  all={allAreas}
                  value={ticket.affectedAreas.map((a) => a.affectedAreaId)}
                  onChange={(ids) => void patch({ affectedAreaIds: ids })}
                />
              </Panel>
              <Panel label="Estimated completion">
                <input
                  type="date"
                  value={ticket.estimatedCompletion ? ticket.estimatedCompletion.slice(0, 10) : ''}
                  onChange={(e) => void patch({ estimatedCompletion: e.target.value || null })}
                  className="input"
                />
              </Panel>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ticket.isCustomerService}
                  onChange={(e) => void patch({ isCustomerService: e.target.checked })}
                  className="mt-0.5"
                />
                <span className="text-xs text-slate-700">
                  <span className="font-medium">Customer service issue</span>
                  <span className="block text-slate-600">Adds <code className="font-mono">[CS]</code> prefix to email subjects</span>
                </span>
              </label>
              <Panel label="Reporter">
                <UserPicker users={users} value={ticket.reporterUserId} onChange={(v) => v && void patch({ reporterUserId: v })} />
              </Panel>
              <CausePanel ticket={ticket} users={users} onChange={patch} />
              <Panel label="Notify lists">
                <ListMultiPicker
                  lists={lists}
                  value={currentListIds}
                  onChange={(ids) => void patch({ notifyListIds: ids })}
                  emptyHint="No lists yet"
                />
                <p className="text-xs text-slate-500 mt-1">Emails sent on create, blocked, and resolve</p>
              </Panel>
              <Panel label="Also notify (ad-hoc)">
                <EmailChipsInput
                  value={currentNotifyEmails}
                  onChange={(emails) => void patch({ notifyEmails: emails })}
                  placeholder="add another email"
                />
                <p className="text-xs text-slate-500 mt-1">One-off CCs for this ticket only</p>
              </Panel>
              <div className="text-xs text-slate-600 border-t border-slate-200 pt-3 space-y-1">
                <div>
                  <strong className="text-slate-700">Created by:</strong> {ticket.createdBy.name}
                </div>
                <div>
                  <strong className="text-slate-700">Created:</strong>{' '}
                  {new Date(ticket.createdAt).toLocaleString()}
                </div>
                {ticket.resolvedAt && (
                  <div>
                    <strong className="text-slate-700">Resolved:</strong>{' '}
                    {new Date(ticket.resolvedAt).toLocaleString()}
                  </div>
                )}
              </div>
              {me?.role === 'ADMIN' && (
                <div className="border-t border-slate-200 pt-3">
                  <button onClick={handleDelete} className="text-xs text-red-600 hover:text-red-700">
                    Delete ticket
                  </button>
                </div>
              )}
            </div>
          </details>
        </aside>
      </div>
    </div>
  );
}

/** Deploy/PR tickets close with the deploy itself — no resolution text or hours needed. */
function isDeployOrPrTicket(ticket: TicketFull): boolean {
  return ticket.isDeployTicket || !!ticket.outgoingLinks?.some((l) => l.linkType === 'DEPLOY_OF');
}

/** Ask for estimated R&D hours; re-asks until a valid number or cancel (null). */
async function promptEstimatedHours(): Promise<number | null> {
  let label = 'How many hours of R&D work went into this ticket? (e.g. 2.5)';
  for (;;) {
    const r = await promptDialog({
      title: 'Estimated hours required',
      label,
      placeholder: 'e.g. 2.5',
      required: true,
    });
    if (r === null) return null;
    const n = Number(r.trim());
    if (r.trim() !== '' && Number.isFinite(n) && n >= 0) return n;
    label = `"${r}" isn't a number of hours. Enter a number, 0 or more (e.g. 2.5).`;
  }
}

async function handleStatusChange(
  newStatus: TicketStatus,
  ticket: TicketFull,
  patch: (b: Parameters<typeof Tickets.patch>[1]) => Promise<void>,
  setError: (e: string | null) => void,
) {
  // Moving to Resolved or Code review requires a resolution and (except on
  // deploy/PR tickets) estimated hours. Prompt for whichever is missing.
  const resolutionGate =
    (newStatus === 'RESOLVED' || newStatus === 'PENDING_CODE_REVIEW') && ticket.status !== newStatus;
  if (resolutionGate) {
    const body: Parameters<typeof Tickets.patch>[1] = { status: newStatus };
    if (!ticket.resolution || !ticket.resolution.trim()) {
      const r = await promptDialog({
        title: 'Resolution required',
        label:
          newStatus === 'RESOLVED'
            ? 'A resolution is required to mark this Resolved. What was done to fix this?'
            : 'A resolution is required before Code review. What was done to fix this?',
        required: true,
        multiline: true,
      });
      if (r === null) {
        setError('Resolution required — status not changed.');
        return;
      }
      body.resolution = r.trim();
    }
    if (ticket.estimatedHours == null && !isDeployOrPrTicket(ticket)) {
      const hours = await promptEstimatedHours();
      if (hours === null) {
        setError('Estimated hours required — status not changed.');
        return;
      }
      body.estimatedHours = hours;
    }
    await patch(body);
    return;
  }
  if (newStatus === 'BLOCKED' && ticket.status !== 'BLOCKED') {
    // A reason is required to move into BLOCKED. Use the stored reason if it already exists
    // (so re-blocking a previously-blocked ticket doesn't re-prompt), otherwise ask now.
    if (!ticket.blockedReason || !ticket.blockedReason.trim()) {
      const r = await promptDialog({
        title: 'What’s blocking this ticket?',
        label: 'A reason is required to mark this Blocked.',
        required: true,
        multiline: true,
      });
      if (r === null) {
        setError('Blocked reason required — status not changed.');
        return;
      }
      await patch({ status: newStatus, blockedReason: r.trim() });
      return;
    }
  }
  await patch({ status: newStatus });
}

function ReviewActionBar({
  ticket,
  onResolved,
  onReopen,
}: {
  ticket: TicketFull;
  onResolved: () => void;
  onReopen: (reason: string) => Promise<void>;
}) {
  const [reopenOpen, setReopenOpen] = useState(false);
  const audience =
    ticket.status === 'PENDING_REPORTER_REVIEW' ? 'reporter' : 'customer';
  return (
    <>
      <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
        <p className="font-semibold mb-2">
          Verify the fix before marking resolved.{' '}
          <span className="font-normal text-emerald-800">
            Confirm the issue is no longer happening
            {audience === 'customer' ? ' with the customer' : ''} before clicking Resolved. If it isn't fixed, click Not resolved and tell us what's still wrong.
          </span>
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onResolved}
            className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
          >
            Resolved
          </button>
          <button
            type="button"
            onClick={() => setReopenOpen(true)}
            className="px-3 py-1.5 rounded-md border border-rose-300 bg-white text-rose-700 text-xs font-medium hover:bg-rose-50"
          >
            Not resolved
          </button>
        </div>
      </div>
      {reopenOpen && (
        <NotResolvedDialog
          onCancel={() => setReopenOpen(false)}
          onSubmit={async (reason) => {
            await onReopen(reason);
            setReopenOpen(false);
          }}
        />
      )}
    </>
  );
}

function BlockedReasonBanner({
  ticket,
  onChange,
}: {
  ticket: TicketFull;
  onChange: (b: Parameters<typeof Tickets.patch>[1]) => Promise<void>;
}) {
  async function editReason() {
    const next = await promptDialog({
      title: 'Update blocked reason',
      label: 'What’s blocking this ticket?',
      defaultValue: ticket.blockedReason ?? '',
      required: true,
      multiline: true,
    });
    if (next === null) return;
    await onChange({ blockedReason: next.trim() });
  }
  return (
    <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 flex items-start gap-2">
      <span className="font-semibold uppercase tracking-wide text-rose-700">Blocked:</span>
      <span className="flex-1 whitespace-pre-wrap break-words">
        {ticket.blockedReason ?? <em className="text-rose-500">(no reason recorded)</em>}
      </span>
      <button
        type="button"
        onClick={() => void editReason()}
        className="shrink-0 text-rose-700 underline hover:text-rose-900"
      >
        Edit
      </button>
    </div>
  );
}

function Panel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

// Order due date + rush flag. Distinct from the developer's estimated-completion
// field (which lives in the details panel): this is when the customer's order is
// due. Rush renders red when set; the due date renders red when it's today (the
// same condition the API uses to force URGENT priority).
function RushDueBar({
  ticket,
  onChange,
}: {
  ticket: TicketFull;
  onChange: (body: Parameters<typeof Tickets.patch>[1]) => Promise<void>;
}) {
  const dueToday = isDueToday(ticket.dueDate);
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 flex flex-wrap items-center gap-x-6 gap-y-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={ticket.rush}
          onChange={(e) => void onChange({ rush: e.target.checked })}
          className="h-4 w-4"
        />
        <span
          className={
            ticket.rush ? 'text-sm font-semibold text-rose-600' : 'text-sm text-slate-800'
          }
        >
          Rush order
        </span>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Order due</span>
        <input
          type="date"
          value={ticket.dueDate ? ticket.dueDate.slice(0, 10) : ''}
          onChange={(e) => void onChange({ dueDate: e.target.value || null })}
          className={`input text-sm py-1 ${dueToday ? 'text-rose-600 font-semibold' : ''}`}
        />
        {dueToday && <span className="text-xs font-semibold text-rose-600">Due today</span>}
      </label>
    </div>
  );
}

function CustomerCard({
  ticket,
  onChange,
  expanded,
}: {
  ticket: TicketFull;
  onChange: (body: Parameters<typeof Tickets.patch>[1]) => Promise<void>;
  expanded: boolean;
}) {
  const [open, setOpen] = useState(expanded);
  // Keep open if data shows up via SSE
  useEffect(() => {
    if (expanded) setOpen(true);
  }, [expanded]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center justify-between"
      >
        <span className="font-medium">
          Customer impacted{' '}
          {!open && expanded && (
            <span className="text-slate-500 font-normal">
              — {ticket.customerCompany || ticket.customerContact || ticket.customerEmail || ticket.customerPhone}
            </span>
          )}
        </span>
        <span className="text-xs text-slate-500">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-slate-100">
          <CustomerField
            label="Company"
            value={ticket.customerCompany ?? ''}
            onCommit={(v) => onChange({ customerCompany: v || null })}
          />
          <CustomerField
            label="Contact"
            value={ticket.customerContact ?? ''}
            onCommit={(v) => onChange({ customerContact: v || null })}
          />
          <CustomerField
            label="Email"
            type="email"
            value={ticket.customerEmail ?? ''}
            onCommit={(v) => onChange({ customerEmail: v || null })}
          />
          <CustomerField
            label="Phone"
            value={ticket.customerPhone ?? ''}
            onCommit={(v) => onChange({ customerPhone: v || null })}
          />
        </div>
      )}
    </div>
  );
}

function CustomerField({
  label,
  value,
  onCommit,
  type,
}: {
  label: string;
  value: string;
  onCommit: (v: string) => void;
  type?: string;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label className="block">
      <span className="block text-xs text-slate-500 mb-1">{label}</span>
      <input
        type={type}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          if (v !== value) onCommit(v);
        }}
        className="input text-sm"
      />
    </label>
  );
}

function TitleSection({
  ticket,
  onSaved,
}: {
  ticket: TicketFull;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(ticket.title);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(ticket.title);
  }, [ticket.title]);

  async function save() {
    const next = value.trim();
    if (!next) return;
    if (next === ticket.title) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await Tickets.patch(ticket.id, { title: next });
      setEditing(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setValue(ticket.title);
    setEditing(false);
  }

  if (!editing) {
    return (
      <div className="flex items-start gap-2">
        <h1 className="text-xl font-semibold text-slate-900 break-words flex-1 min-w-0">
          {ticket.title}
        </h1>
        <button
          onClick={() => setEditing(true)}
          className="text-slate-500 hover:text-slate-700 p-1 -m-1 shrink-0 mt-1"
          title="Edit title"
          aria-label="Edit title"
          type="button"
        >
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        autoFocus
        maxLength={300}
        className="flex-1 min-w-0 text-xl font-semibold text-slate-900 bg-white border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:border-slate-500"
      />
      <button
        onClick={cancel}
        disabled={saving}
        className="px-2 py-1 rounded-md text-sm text-slate-600 hover:text-slate-900 disabled:opacity-60 shrink-0"
        type="button"
      >
        Cancel
      </button>
      <button
        onClick={() => void save()}
        disabled={saving || !value.trim() || value === ticket.title}
        className="px-3 py-1 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60 shrink-0"
        type="button"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function DescriptionSection({
  ticket,
  onSaved,
}: {
  ticket: TicketFull;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(ticket.description);
  const [saving, setSaving] = useState(false);

  // Pull in fresh remote values when the ticket reloads after a save.
  useEffect(() => {
    setValue(ticket.description);
  }, [ticket.description]);

  async function save() {
    const next = value.trim();
    if (!next) return;
    if (next === ticket.description) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await Tickets.patch(ticket.id, { description: next });
      setEditing(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setValue(ticket.description);
    setEditing(false);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-slate-700">Description</h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-slate-500 hover:text-slate-700 p-1 -m-1"
            title="Edit description"
            aria-label="Edit description"
            type="button"
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>
        )}
      </div>
      {!editing ? (
        <div className="bg-white border border-slate-200 rounded-lg p-4 whitespace-pre-wrap text-sm">
          <TextWithTicketLinks text={ticket.description} />
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
          <TicketLinkTextarea
            value={value}
            onChange={setValue}
            rows={Math.min(20, Math.max(6, value.split('\n').length + 1))}
            className="input text-sm"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={cancel}
              disabled={saving}
              className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:text-slate-900 disabled:opacity-60"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={() => void save()}
              disabled={saving || !value.trim() || value === ticket.description}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
              type="button"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResolutionSection({
  ticket,
  me,
  onSaved,
}: {
  ticket: TicketFull;
  me: UserSummary | null;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(ticket.resolution ?? '');
  const [humanValue, setHumanValue] = useState(ticket.humanResolution ?? '');
  const [dirty, setDirty] = useState(false);
  const [humanDirty, setHumanDirty] = useState(false);
  const [hoursValue, setHoursValue] = useState(ticket.estimatedHours ?? '');
  const [hoursDirty, setHoursDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [reassignToMe, setReassignToMe] = useState(false);
  // Deploy ticket: when user goes to resolve, this captures the pending intent
  // until they answer the "what should happen to the original?" dialog.
  const [deployDialog, setDeployDialog] = useState<{
    asStatus?: TicketStatus;
    originalTicketId: number;
  } | null>(null);

  // A deploy ticket has at least one DEPLOY_OF outgoing link pointing at the
  // original ticket. The UI gates its RESOLVED save flow on a dialog for
  // these so the resolver picks the original's next status.
  const deployLink = ticket.outgoingLinks?.find((l) => l.linkType === 'DEPLOY_OF') ?? null;
  const isDeployTicket = !!deployLink;
  // Hours are required to resolve anything but a deploy/PR ticket.
  const hoursRequired = !isDeployOrPrTicket(ticket);

  useEffect(() => {
    setValue(ticket.resolution ?? '');
    setHumanValue(ticket.humanResolution ?? '');
    setHoursValue(ticket.estimatedHours ?? '');
    setDirty(false);
    setHumanDirty(false);
    setHoursDirty(false);
    setReassignToMe(false);
  }, [ticket.resolution, ticket.humanResolution, ticket.estimatedHours, ticket.assigneeUserId]);

  const parsedHours = hoursValue.trim() === '' ? null : Number(hoursValue);
  const hoursInvalid = parsedHours !== null && (!Number.isFinite(parsedHours) || parsedHours < 0);
  const anyDirty = dirty || humanDirty || hoursDirty;

  // Explicit status overrides the API's auto-resolve when the resolution text is non-empty.
  async function save(
    asStatus?: TicketStatus,
    extra?: { postDeployOriginalStatus?: PatchTicketBody['postDeployOriginalStatus'] },
  ) {
    setSaving(true);
    try {
      await Tickets.patch(ticket.id, {
        ...(dirty ? { resolution: value || null } : {}),
        ...(humanDirty ? { humanResolution: humanValue || null } : {}),
        ...(hoursDirty ? { estimatedHours: parsedHours } : {}),
        ...(asStatus ? { status: asStatus } : {}),
        ...(reassignToMe && me ? { assigneeUserId: me.id } : {}),
        ...(extra?.postDeployOriginalStatus
          ? { postDeployOriginalStatus: extra.postDeployOriginalStatus }
          : {}),
      });
      setDirty(false);
      setHumanDirty(false);
      setHoursDirty(false);
      setReassignToMe(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  // Intercept saves that would resolve a deploy ticket — pop the dialog
  // first so the resolver picks the original's next status. asStatus is
  // undefined when the auto-resolve path will fire (non-empty resolution).
  function attemptSave(asStatus?: TicketStatus) {
    const willResolve =
      asStatus === 'RESOLVED' || (asStatus === undefined && willAutoResolve);
    if (isDeployTicket && willResolve && deployLink) {
      setDeployDialog({
        asStatus,
        originalTicketId: deployLink.linkedTicketId,
      });
      return;
    }
    void save(asStatus);
  }

  async function regenerate() {
    setRegenerating(true);
    try {
      const res = await Tickets.humanizeResolution(ticket.id);
      setHumanValue(res.ticket.humanResolution ?? '');
      setHumanDirty(false);
      onSaved();
    } catch (err) {
      console.error('regenerate failed', err);
    } finally {
      setRegenerating(false);
    }
  }

  const isResolved = ticket.status === 'RESOLVED';
  const isReadyToDeploy = ticket.status === 'READY_TO_DEPLOY';
  const isUnresolved = !isResolved;
  // Saving a non-empty resolution on a non-resolved ticket will flip it to Resolved
  // (the API auto-resolves). Human-only edits don't trigger that.
  const willAutoResolve = isUnresolved && dirty && value.trim().length > 0;
  // Offer "Ready to deploy" as an alternative when the user is about to auto-resolve
  // but the change actually still needs deployment. Suppressed on a deploy
  // ticket — that's the wrong workflow there.
  const offerReadyToDeploy = willAutoResolve && !isReadyToDeploy && !isDeployTicket;
  // Block the resolve path (not Ready-to-deploy, which doesn't need hours)
  // until estimated hours are filled in.
  const hoursBlockResolve = willAutoResolve && hoursRequired && parsedHours === null;
  // Offer to reassign the ticket to the resolver when they aren't already the assignee.
  // Default OFF — user opts in explicitly.
  const offerReassign = !!me && anyDirty && ticket.assigneeUserId !== me.id;
  const canRegenerate = (value.trim().length > 0) && !regenerating && !saving;

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-1">Resolution / fix</h3>
      <div className={`bg-white border rounded-lg p-3 space-y-3 ${isResolved ? 'border-emerald-300' : 'border-slate-200'}`}>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Internal (technical)
          </label>
          <TicketLinkTextarea
            value={value}
            onChange={(v) => {
              setValue(v);
              setDirty(true);
            }}
            rows={4}
            placeholder="Describe what was done to fix this ticket. Type # to link a ticket."
            className="input"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="block text-xs font-medium text-slate-500">
              Customer-friendly version <span className="text-slate-500">(used in the resolution email)</span>
            </label>
            <button
              type="button"
              onClick={() => void regenerate()}
              disabled={!canRegenerate}
              title={value.trim().length > 0 ? 'Rewrite from the internal resolution using AI' : 'Save an internal resolution first'}
              className="text-xs px-2 py-0.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
          <textarea
            value={humanValue}
            onChange={(e) => {
              setHumanValue(e.target.value);
              setHumanDirty(true);
            }}
            rows={4}
            placeholder="Auto-fills from the internal resolution when you save. Edit freely; this is what customers will read."
            className="input w-full"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Estimated hours{hoursRequired && <span className="text-red-600"> *</span>}{' '}
            <span className="text-slate-500">
              (internal only{hoursRequired ? ' — required to resolve' : ''})
            </span>
          </label>
          <input
            type="number"
            min={0}
            step={0.25}
            inputMode="decimal"
            value={hoursValue}
            onChange={(e) => {
              setHoursValue(e.target.value);
              setHoursDirty(true);
            }}
            placeholder="e.g. 2.5"
            className="input w-32"
          />
          {hoursInvalid && (
            <p className="text-xs text-red-600 mt-1">Enter a number of hours, 0 or more.</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          {hoursBlockResolve ? (
            <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
              Enter <strong>estimated hours</strong> to resolve this ticket.
            </span>
          ) : willAutoResolve ? (
            <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
              Saving will mark this ticket <strong>Resolved</strong> and send the resolution email.
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3 flex-wrap">
            {offerReassign && (
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reassignToMe}
                  onChange={(e) => setReassignToMe(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                Reassign this ticket to me
              </label>
            )}
            {offerReadyToDeploy && (
              <button
                type="button"
                onClick={() => attemptSave('READY_TO_DEPLOY')}
                disabled={!anyDirty || saving || hoursInvalid}
                className="px-3 py-1.5 rounded-md border border-cyan-300 bg-cyan-50 text-cyan-800 text-sm hover:bg-cyan-100 disabled:opacity-60"
              >
                Save & mark Ready to deploy
              </button>
            )}
            <button
              onClick={() => attemptSave()}
              disabled={!anyDirty || saving || hoursInvalid || hoursBlockResolve}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? 'Saving…' : willAutoResolve ? 'Save & resolve' : anyDirty ? 'Save resolution' : 'Saved'}
            </button>
          </div>
        </div>
      </div>
      {deployDialog && (
        <DeployResolveDialog
          originalTicketId={deployDialog.originalTicketId}
          onCancel={() => setDeployDialog(null)}
          onConfirm={async (choice) => {
            const intent = deployDialog;
            setDeployDialog(null);
            await save(intent.asStatus, { postDeployOriginalStatus: choice });
          }}
        />
      )}
    </div>
  );
}

function DeployResolveDialog({
  originalTicketId,
  onCancel,
  onConfirm,
}: {
  originalTicketId: number;
  onCancel: () => void;
  onConfirm: (
    choice: NonNullable<PatchTicketBody['postDeployOriginalStatus']>,
  ) => Promise<void> | void;
}) {
  const [choice, setChoice] =
    useState<NonNullable<PatchTicketBody['postDeployOriginalStatus']>>('PENDING_CUSTOMER_REVIEW');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await onConfirm(choice);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-md w-full">
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Deploy approved</h3>
          <p className="text-xs text-slate-500 mt-1">
            What should happen to the original ticket{' '}
            <Link to={`/tickets/${originalTicketId}`} className="text-brand-600 hover:underline">
              #{originalTicketId}
            </Link>
            ?
          </p>
        </div>
        <div className="p-4 space-y-2">
          {[
            {
              value: 'PENDING_CUSTOMER_REVIEW' as const,
              label: 'Pending customer review',
              detail: 'Original goes to customer for sign-off.',
            },
            {
              value: 'PENDING_REPORTER_REVIEW' as const,
              label: 'Pending reporter review',
              detail: 'Internal reporter validates the fix.',
            },
            {
              value: 'RESOLVED' as const,
              label: 'Resolved',
              detail: 'Close the original — no further review needed.',
            },
            {
              value: 'leave' as const,
              label: 'Leave alone',
              detail: 'Resolve only the deploy ticket; the original stays as-is.',
            },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`block border rounded-md p-2 cursor-pointer ${
                choice === opt.value
                  ? 'border-brand-300 bg-brand-50'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="post-deploy"
                  value={opt.value}
                  checked={choice === opt.value}
                  onChange={() => setChoice(opt.value)}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">{opt.label}</div>
                  <div className="text-xs text-slate-500">{opt.detail}</div>
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="p-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting ? 'Resolving…' : 'Resolve & apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AiNotesTab({ ticket, onChange }: { ticket: TicketFull; onChange: () => void }) {
  const [clearing, setClearing] = useState(false);

  async function clearNotes() {
    const ok = await confirmDialog({
      title: 'Clear AI notes?',
      body: 'The AI notes on this ticket will be removed.',
      tone: 'danger',
      confirmLabel: 'Clear',
    });
    if (!ok) return;
    setClearing(true);
    try {
      await Tickets.clearAiNotes(ticket.id);
      onChange();
    } finally {
      setClearing(false);
    }
  }

  if (!ticket.aiNotes) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
        <p className="mb-1">No AI notes yet.</p>
        <p className="text-xs text-slate-500">
          Ask Claude (via the MCP server) to assess this ticket — the result will appear here.
        </p>
      </div>
    );
  }

  const authorName = ticket.aiNotesAuthor?.name ?? 'Unknown';
  const when = ticket.aiNotesUpdatedAt
    ? new Date(ticket.aiNotesUpdatedAt).toLocaleString()
    : '';

  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-200 text-xs text-slate-500">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 font-medium">
            AI
          </span>
          {ticket.aiNotesModel && <span className="text-slate-600">{ticket.aiNotesModel}</span>}
          <span>·</span>
          <span>Triggered by {authorName}</span>
          {when && (
            <>
              <span>·</span>
              <span>{when}</span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => void clearNotes()}
          disabled={clearing}
          className="text-xs px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {clearing ? 'Clearing…' : 'Clear'}
        </button>
      </div>
      <div className="px-4 py-3 prose prose-sm max-w-none prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1.5 prose-li:my-0">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{ticket.aiNotes}</ReactMarkdown>
      </div>
    </div>
  );
}

function StepsTab({ ticketId, initial, onSaved }: { ticketId: number; initial: string; onSaved: () => void }) {
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setValue(initial);
    setDirty(false);
  }, [initial]);

  async function save() {
    setSaving(true);
    try {
      await Tickets.patch(ticketId, { stepsToReproduce: value || null });
      setDirty(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
      <TicketLinkTextarea
        value={value}
        onChange={(v) => {
          setValue(v);
          setDirty(true);
        }}
        rows={10}
        placeholder="1. …&#10;2. …&#10;3. … (type # to link a ticket)"
        className="input font-mono text-sm"
      />
      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

function CausePanel({
  ticket,
  users,
  onChange,
}: {
  ticket: TicketFull;
  users: UserSummary[];
  onChange: (body: Parameters<typeof Tickets.patch>[1]) => Promise<void>;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-xs text-slate-500">Root cause</label>
      <select
        value={ticket.cause}
        onChange={(e) => void onChange({ cause: e.target.value as TicketCause })}
        className="input"
      >
        <option value="NEW_PROBLEM">New problem</option>
        <option value="LEGACY">Legacy</option>
        <option value="PREVIOUS_FIX">Caused by previous fix</option>
        <option value="PERSON">Person</option>
        <option value="CUSTOMER">Customer</option>
      </select>
      {ticket.cause === 'PERSON' && (
        <UserPicker
          users={users}
          value={ticket.causeUserId}
          onChange={(v) => void onChange({ causeUserId: v })}
          allowEmpty
          emptyLabel="(pick a person)"
        />
      )}
      {ticket.cause === 'PREVIOUS_FIX' && (
        <CauseTicketPicker
          currentId={ticket.causeTicketId}
          onChange={(v) => void onChange({ causeTicketId: v })}
        />
      )}
      {ticket.causeTicket && (
        <Link
          to={`/tickets/${ticket.causeTicket.id}`}
          className="block text-xs text-brand-600 hover:text-brand-700 truncate"
        >
          → #{ticket.causeTicket.id} {ticket.causeTicket.title}
        </Link>
      )}
    </div>
  );
}

function CauseTicketPicker({
  currentId,
  onChange,
}: {
  currentId: number | null;
  onChange: (v: number | null) => void;
}) {
  const [val, setVal] = useState(currentId?.toString() ?? '');
  useEffect(() => setVal(currentId?.toString() ?? ''), [currentId]);
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const n = Number(val.replace(/^#/, ''));
        onChange(Number.isFinite(n) && n > 0 ? n : null);
      }}
      placeholder="Ticket # (e.g. 42)"
      className="input text-sm"
    />
  );
}

function AffectedAreasPicker({
  all,
  value,
  onChange,
}: {
  all: AffectedAreaRow[];
  value: number[];
  onChange: (next: number[]) => void;
}) {
  const selected = new Set(value);
  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  }
  // Show all active areas. Plus any inactive ones the ticket still references, so
  // a user can untick them.
  const visible = all.filter((a) => a.isActive || selected.has(a.id));
  if (visible.length === 0) {
    return <p className="text-xs text-slate-500">{'No affected areas defined yet. Admins can manage these in Admin → Affected areas.'}</p>;
  }
  return (
    <div className="border border-slate-300 rounded-md p-2 bg-white space-y-1 text-xs max-h-48 overflow-y-auto">
      {visible.map((a) => (
        <label key={a.id} className="flex items-center gap-1.5 cursor-pointer hover:text-slate-900">
          <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
          <span className={a.isActive ? '' : 'text-slate-500 italic'}>
            {a.name}{!a.isActive && ' (retired)'}
          </span>
        </label>
      ))}
    </div>
  );
}

function RankInput({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (next: number | null) => void;
}) {
  const [draft, setDraft] = useState(value?.toString() ?? '');
  useEffect(() => setDraft(value?.toString() ?? ''), [value]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === '') {
      if (value !== null) onSave(null);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 1) {
      setDraft(value?.toString() ?? '');
      return;
    }
    if (n !== value) onSave(n);
  }

  return (
    <input
      type="number"
      min={1}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setDraft(value?.toString() ?? '');
      }}
      placeholder="—"
      className="input w-24 tabular-nums"
    />
  );
}

function ActivityFeed({ ticket }: { ticket: TicketFull }) {
  type Row =
    | { kind: 'comment'; t: number; item: (typeof ticket.comments)[number] }
    | { kind: 'event'; t: number; item: (typeof ticket.events)[number] };
  const rows: Row[] = [
    ...ticket.comments.map((c) => ({
      kind: 'comment' as const,
      t: new Date(c.createdAt).getTime(),
      item: c,
    })),
    ...ticket.events.map((e) => ({
      kind: 'event' as const,
      t: new Date(e.createdAt).getTime(),
      item: e,
    })),
  ].sort((a, b) => a.t - b.t);

  if (rows.length === 0) return <p className="text-sm text-slate-500">No activity yet.</p>;

  return (
    <ul className="space-y-3">
      {rows.map((row) =>
        row.kind === 'comment' ? (
          <li
            key={`c-${row.item.id}`}
            id={`comment-${row.item.id}`}
            className="bg-white border border-slate-200 rounded-md p-3 text-sm scroll-mt-16 target:ring-2 target:ring-brand-400"
          >
            <div className="text-xs text-slate-500 mb-1 flex flex-wrap items-center gap-1.5">
              <strong className="text-slate-700">{row.item.author.name}</strong>
              <span>· {new Date(row.item.createdAt).toLocaleString()}</span>
              {row.item.mentions.length > 0 && (
                <span className="text-slate-500">
                  → mentioned{' '}
                  {row.item.mentions
                    .map((m) => `@${m.user.name}`)
                    .join(', ')}
                </span>
              )}
            </div>
            <div className="whitespace-pre-wrap">
              <TextWithTicketLinks text={row.item.body} />
            </div>
            {row.item.attachments && row.item.attachments.length > 0 && (
              <CommentAttachments attachments={row.item.attachments} />
            )}
          </li>
        ) : (
          <li key={`e-${row.item.id}`} className="text-xs text-slate-500 px-1">
            <strong className="text-slate-700">{row.item.actor.name}</strong>{' '}
            {formatEvent(row.item.eventType, row.item.fromValue, row.item.toValue)} ·{' '}
            {new Date(row.item.createdAt).toLocaleString()}
          </li>
        ),
      )}
    </ul>
  );
}

function PendingImageStrip({
  items,
  onRemove,
}: {
  items: PendingImage[];
  onRemove: (tempId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((p) => (
        <div
          key={p.tempId}
          className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200 bg-slate-50"
        >
          <img src={p.previewUrl} alt={p.filename} className="w-full h-full object-cover" />
          {!p.attachmentId && !p.error && (
            <div className="absolute inset-x-0 bottom-0 h-1 bg-slate-300">
              <div className="h-full bg-brand-500" style={{ width: `${p.progress * 100}%` }} />
            </div>
          )}
          {p.error && (
            <div
              className="absolute inset-0 bg-red-900/60 text-white text-xs flex items-center justify-center text-center p-1"
              title={p.error}
            >
              upload failed
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(p.tempId)}
            className="absolute top-0.5 right-0.5 bg-slate-900/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-slate-900"
            aria-label="Remove"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function CommentAttachments({ attachments }: { attachments: AttachmentRecord[] }) {
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const a of attachments) {
        if (!a.mimeType.startsWith('image/') || thumbs[a.id]) continue;
        try {
          const url = await getAttachmentDownloadURL(a.storagePath);
          if (!cancelled) setThumbs((t) => ({ ...t, [a.id]: url }));
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachments, thumbs]);
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a) =>
        a.mimeType.startsWith('image/') && thumbs[a.id] ? (
          <a
            key={a.id}
            href={thumbs[a.id]}
            target="_blank"
            rel="noreferrer"
            className="block w-24 h-24 rounded-md overflow-hidden border border-slate-200 bg-slate-50"
          >
            <img src={thumbs[a.id]} alt={a.filename} className="w-full h-full object-cover" />
          </a>
        ) : (
          <button
            key={a.id}
            type="button"
            onClick={async () => {
              const url =
                thumbs[a.id] ?? (await getAttachmentDownloadURL(a.storagePath));
              window.open(url, '_blank');
            }}
            className="px-2 py-1 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          >
            📎 {a.filename}
          </button>
        ),
      )}
    </div>
  );
}

function formatEvent(type: string, from: unknown, to: unknown): string {
  const f = JSON.stringify(from);
  const t = JSON.stringify(to);
  switch (type) {
    case 'CREATED':
      return 'created the ticket';
    case 'STATUS_CHANGED':
      return `changed status ${f} → ${t}`;
    case 'PRIORITY_CHANGED':
      return `changed priority ${f} → ${t}`;
    case 'AREA_CHANGED':
      return `changed area ${f} → ${t}`;
    case 'ASSIGNEE_CHANGED':
      return `reassigned ${f} → ${t}`;
    case 'REPORTER_CHANGED':
      return `changed reporter ${f} → ${t}`;
    case 'BLAME_CHANGED':
      return `changed cause ${f} → ${t}`;
    case 'CUSTOMER_SERVICE_CHANGED':
      return (to as { isCustomerService?: boolean })?.isCustomerService
        ? 'marked as customer service'
        : 'unmarked as customer service';
    case 'LINK_ADDED':
      return `added link ${t}`;
    case 'LINK_REMOVED':
      return `removed link ${f}`;
    case 'COMMENT_ADDED':
      return 'commented';
    case 'ATTACHMENT_ADDED':
      return 'added an attachment';
    case 'EMAIL_SENT':
      return `sent email (${(to as { kind?: string })?.kind ?? '?'})`;
    default:
      return type.toLowerCase();
  }
}
